import React, { useEffect, useState } from "react";
import { Alert, Avatar, Button, Col, Row, Space, Typography } from "antd";
import DisplayNextOpenings from "../DIsplayNextOpenings";
import ServiceConsultation from "../SpecialServiceMessage";

import TimeButton from "@/app/components/TimeButton";
import { useBookingApi } from "@/app/components/NewAppointmentModal/bookingApi";
import {
  UserOpeningsData,
  UserData,
  ServiceOption,
  Service,
} from "@/lib/api/types";
import OpeningLoading from "@/app/components/OpeningLoading";
import { MinBookingNoticeNote } from "@/app/components/BookingLimitNotes";
import { AppointmentForm } from "@/lib/hooks/useAppointment";
import { LocationServiceSelectionError } from "@/lib/api/openingsLocations";

const LocationTag = ({
  name,
  address,
  image,
}: {
  name: string;
  address?: string;
  image?: string;
}) => (
  <Space align="center" size={8}>
    <Avatar size="small" shape="square" src={image} style={{ flexShrink: 0 }}>
      {name.charAt(0).toUpperCase()}
    </Avatar>
    <Space orientation="vertical" size={0}>
      <Typography.Text>{name}</Typography.Text>
      {address && (
        <Typography.Text type="secondary" className="is-size-7">
          {address}
        </Typography.Text>
      )}
    </Space>
  </Space>
);

interface StylistOpeningsProps {
  userId: string;
  userPathId: string;
  userName: string;
  date: string;
  refreshCount: number;
  services: (Service & {
    option?: ServiceOption;
  })[];
  locationMeta?: UserData["schedules"];
  isTodayOnVacation?: boolean;
  onTimeClick: (args: AppointmentForm) => void;
  onRefresh?: () => void;
}

export const StylistOpenings = ({
  userId,
  userPathId,
  userName,
  date,
  services,
  refreshCount,
  locationMeta,
  isTodayOnVacation,
  onTimeClick,
  onRefresh,
}: StylistOpeningsProps) => {
  const bookingApi = useBookingApi();
  const [isLoading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState<UserOpeningsData[]>([]);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasSelectionMismatch, setHasSelectionMismatch] = useState(false);

  const locationAddressById = new Map(
    (locationMeta ?? []).map((s) => [s.locationId, s.locationAddress]),
  );
  const locationImageById = new Map(
    (locationMeta ?? []).map((s) => [s.locationId, s.locationImage]),
  );

  let openingCount = 0;
  const servicesRequiredConsultation: Service[] = [];
  services.forEach((service) => {
    if (service.hasConsultation) {
      servicesRequiredConsultation.push(service);
    }
  });

  const handleTimeClick = React.useCallback(
    (
      schedule: UserOpeningsData["schedule"],
      time: string,
      slotServices: UserOpeningsData["services"],
    ) => {
      onTimeClick({
        // A stylist working at two businesses has a different memberId in
        // each; book with the one this slot was computed for.
        userId: schedule.memberId ?? userId,
        stylistName: userName,
        locationName: schedule.locationName,
        // The registry schedule id for this slot's location. `scheduleId`
        // below is the MEMBER'S own schedule id and does not resolve in the
        // registry, so without this the server has only one resolvable
        // identifier and cannot detect a member/location disagreement.
        locationId: schedule.locationId,
        scheduleId: schedule._id,
        services: slotServices ?? services,
        time,
        date,
      });
    },
    [userId, userName, services, date, onTimeClick],
  );

  useEffect(() => {
    if (services.length === 0) return; // ✅ Only run if services exist

    // Overlapping fetches settle out of order: a slow response for a previous
    // date/selection must not overwrite the newer one, or a click books that
    // stale slot against the CURRENT date prop. Same guard as MemberOpenings.
    let isActive = true;

    (async () => {
      setLoading(true);
      setHasLoadError(false);
      setHasSelectionMismatch(false);
      try {
        const _res = await bookingApi.getOpeningsByDuration(
          userPathId,
          date,
          services,
        );
        if (isActive) setSchedules(_res);
      } catch (error) {
        if (isActive) {
          // Never leave a previous date's slots on screen after a failure — a
          // click would book that stale slot against the CURRENT date prop.
          setSchedules([]);
          if (error instanceof LocationServiceSelectionError) {
            setHasSelectionMismatch(true);
          } else {
            console.error(error);
            setHasLoadError(true);
          }
        }
      }
      if (isActive) setLoading(false);
    })();

    return () => {
      isActive = false;
    };
  }, [bookingApi, userPathId, date, refreshCount, services]);

  if (servicesRequiredConsultation.length > 0)
    return (
      <ServiceConsultation
        userId={userId}
        userName={userName}
        services={servicesRequiredConsultation}
        locations={locationMeta}
      />
    );

  if (isLoading) return <OpeningLoading />;

  if (hasSelectionMismatch) {
    return (
      <Alert
        type="warning"
        showIcon
        message="The selected service is no longer available."
        description="Please choose a different service or option."
      />
    );
  }

  if (hasLoadError) {
    return (
      <Alert
        type="error"
        showIcon
        title="We couldn't load availability."
        description="Please check your connection and try again."
        action={
          onRefresh ? (
            <Button size="small" onClick={onRefresh}>
              Try again
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <Space orientation="vertical" style={{ width: "100%" }}>
      {isTodayOnVacation ? (
        <Alert type="success" message="On vacation" />
      ) : (
        <>
          {schedules.map((item) => {
            const { schedule, openings } = item;
            if (!openings || openings.length === 0) {
              return null;
            } else {
              openingCount++;
              return (
                <Space
                  orientation="vertical"
                  style={{ width: "100%" }}
                  key={`${schedule.locationId}:${schedule._id}:${schedule.memberId ?? ""}`}
                >
                  <LocationTag
                    name={schedule.locationName}
                    address={locationAddressById.get(schedule.locationId)}
                    image={locationImageById.get(schedule.locationId)}
                  />
                  {/* This location's own resolved services: a stylist who
                      works at two businesses can have a notice at one and
                      not the other, so the note sits with the slots it
                      applies to, not with the canonical menu above. */}
                  <MinBookingNoticeNote services={item.services ?? services} />
                  <Row gutter={[8, 8]}>
                    {openings.map((time: string, i: number) => {
                      return (
                        <Col
                          key={`${schedule.locationId}:${schedule._id}:${time}:${i}`}
                          span={6}
                        >
                          <TimeButton
                            onClick={() =>
                              handleTimeClick(schedule, time, item.services)
                            }
                            time={time}
                            isSelected={false}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </Space>
              );
            }
          })}
        </>
      )}
      {openingCount === 0 && (
        <DisplayNextOpenings
          userId={userId}
          userName={userName}
          userPathName={userPathId}
          date={date}
          services={services}
          onTimeClick={onTimeClick}
          onRefresh={onRefresh}
        />
      )}
    </Space>
  );
};

export default React.memo(StylistOpenings);
